import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Ticket, Phone, X, ArrowUp, ArrowDown, CornerDownLeft } from 'lucide-react';
import { cn } from '@pp/shared/utils/cn';
import { usePermissions } from '../../contexts/PermissionContext';
import {
  searchablePages,
  searchableCommands,
  filterSearchableItems,
  sortByRelevance,
  type SearchableItem,
} from '../../utils/searchData';
import { searchAccounts, type CustomerAccount } from '../../api/accountsApi';
import { getLeads } from '../../api/leadsApi';
import { getTickets, type Ticket as SupportTicket } from '../../api/ticketsApi';
import { getLocateTickets } from '../../api/locateTicketsApi';
import type { Lead } from '../../types';

// ==================== Types ====================

interface QuickSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RecordResult {
  type: 'customer' | 'lead' | 'ticket' | 'locateTicket';
  id: string;
  name: string;
  subtitle?: string;
  href: string;
}

interface SearchCategory {
  type: 'pages' | 'commands' | 'records';
  label: string;
  items: (SearchableItem | RecordResult)[];
}

// ==================== Constants ====================

const RECENT_SEARCHES_KEY = 'quick-search-recent';
const MAX_RECENT_SEARCHES = 5;
const DEBOUNCE_MS = 300;

// ==================== Component ====================

export function QuickSearch({ isOpen, onClose }: QuickSearchProps) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearchingRecords, setIsSearchingRecords] = useState(false);
  const [recordResults, setRecordResults] = useState<RecordResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Save recent search
  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setRecentSearches(prev => {
      const filtered = prev.filter(s => s !== searchQuery);
      const updated = [searchQuery, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Filter pages and commands
  const filteredPages = useMemo(() => {
    const items = filterSearchableItems(searchablePages, query, hasPermission);
    return query ? sortByRelevance(items, query) : items;
  }, [query, hasPermission]);

  const filteredCommands = useMemo(() => {
    const items = filterSearchableItems(searchableCommands, query, hasPermission);
    return query ? sortByRelevance(items, query) : items;
  }, [query, hasPermission]);

  // Search records with debounce
  useEffect(() => {
    if (!query || query.length < 2) {
      setRecordResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearchingRecords(true);
      const results: RecordResult[] = [];

      try {
        // Search customers
        if (hasPermission('accounts.view')) {
          const customers = await searchAccounts(query, 5);
          results.push(...customers.map((c: CustomerAccount) => ({
            type: 'customer' as const,
            id: c.id,
            name: c.primaryContactName || `Account #${c.accountNumber}`,
            subtitle: c.primaryContactEmail || c.serviceAddress,
            href: `/dashboard/accounts/${c.id}`,
          })));
        }

        // Search leads
        if (hasPermission('leads.view')) {
          const leadsResponse = await getLeads({ searchTerm: query, page: 1, pageSize: 5 });
          results.push(...leadsResponse.items.map((l: Lead) => ({
            type: 'lead' as const,
            id: l.id,
            name: `${l.firstName} ${l.lastName}`,
            subtitle: l.email || l.phone,
            href: `/dashboard/leads/${l.id}`,
          })));
        }

        // Search tickets
        if (hasPermission('tickets.view')) {
          const ticketsResponse = await getTickets({ search: query, page: 1, pageSize: 5 });
          results.push(...ticketsResponse.items.map((t: SupportTicket) => ({
            type: 'ticket' as const,
            id: t.id,
            name: `#${t.ticketNumber}: ${t.title}`,
            subtitle: t.customerName,
            href: `/dashboard/tickets/${t.id}`,
          })));
        }

        // Search locate tickets
        if (hasPermission('locate-tickets.view')) {
          const locateResponse = await getLocateTickets({ searchTerm: query, page: 1, pageSize: 5 });
          results.push(...locateResponse.items.map((lt) => ({
            type: 'locateTicket' as const,
            id: lt.id,
            name: `Locate #${lt.ticketNumber}`,
            subtitle: lt.address,
            href: `/dashboard/locate-tickets/${lt.id}`,
          })));
        }
      } catch (error) {
        console.error('Error searching records:', error);
      }

      setRecordResults(results);
      setIsSearchingRecords(false);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [query, hasPermission]);

  // Build categories for display
  const categories = useMemo((): SearchCategory[] => {
    const cats: SearchCategory[] = [];

    // Show pages (limit to 5 when searching)
    const pagesToShow = query ? filteredPages.slice(0, 5) : filteredPages.slice(0, 8);
    if (pagesToShow.length > 0) {
      cats.push({ type: 'pages', label: 'Pages', items: pagesToShow });
    }

    // Show commands (limit to 5 when searching)
    const commandsToShow = query ? filteredCommands.slice(0, 5) : filteredCommands.slice(0, 5);
    if (commandsToShow.length > 0) {
      cats.push({ type: 'commands', label: 'Commands', items: commandsToShow });
    }

    // Show records when searching
    if (recordResults.length > 0) {
      cats.push({ type: 'records', label: 'Records', items: recordResults });
    }

    return cats;
  }, [filteredPages, filteredCommands, recordResults, query]);

  // Flatten items for keyboard navigation
  const flatItems = useMemo(() => {
    return categories.flatMap(cat => cat.items);
  }, [categories]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, recordResults.length]);

  // Handle item selection
  const handleSelect = useCallback((item: SearchableItem | RecordResult) => {
    saveRecentSearch(query);

    if ('href' in item && item.href) {
      navigate(item.href);
      onClose();
    }
  }, [navigate, onClose, query, saveRecentSearch]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, flatItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatItems[selectedIndex]) {
          handleSelect(flatItems[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [flatItems, selectedIndex, onClose, handleSelect]);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setRecordResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Get icon for record type
  const getRecordIcon = (type: RecordResult['type']) => {
    switch (type) {
      case 'customer': return Users;
      case 'lead': return Users;
      case 'ticket': return Ticket;
      case 'locateTicket': return Phone;
    }
  };

  // Track current index for rendering
  let currentIndex = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full max-w-xl bg-surface border border-border rounded-xl shadow-2xl',
          'animate-in fade-in zoom-in-95 duration-150'
        )}
        onKeyDown={handleKeyDown}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search size={20} className="text-text-secondary flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, records, or commands..."
            className="flex-1 bg-transparent text-text placeholder-text-secondary outline-none text-base"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-surface-hover rounded"
            >
              <X size={16} className="text-text-secondary" />
            </button>
          )}
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-[50vh] overflow-y-auto py-2"
        >
          {categories.length === 0 && query && !isSearchingRecords && (
            <div className="px-4 py-8 text-center text-text-secondary">
              No results found for "{query}"
            </div>
          )}

          {categories.map((category) => (
            <div key={category.type} className="mb-2">
              {/* Category Header */}
              <div className="px-4 py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                {category.label}
                {category.type === 'records' && isSearchingRecords && (
                  <span className="ml-2 text-text-secondary animate-pulse">Loading...</span>
                )}
              </div>

              {/* Category Items */}
              {category.items.map((item) => {
                const itemIndex = currentIndex++;
                const isSelected = itemIndex === selectedIndex;

                // Determine icon and display
                let Icon: React.ComponentType<{ size?: number; className?: string }>;
                let displayName: string;
                let displaySubtitle: string | undefined;
                let typeLabel: string | undefined;

                if ('type' in item && (item.type === 'customer' || item.type === 'lead' || item.type === 'ticket' || item.type === 'locateTicket')) {
                  // Record result
                  const record = item as RecordResult;
                  Icon = getRecordIcon(record.type);
                  displayName = record.name;
                  displaySubtitle = record.subtitle;
                  typeLabel = record.type === 'customer' ? 'Customer' :
                              record.type === 'lead' ? 'Lead' :
                              record.type === 'ticket' ? 'Ticket' :
                              'Locate Ticket';
                } else {
                  // Page or command
                  const searchItem = item as SearchableItem;
                  Icon = searchItem.icon;
                  displayName = searchItem.name;
                  if (searchItem.type === 'page') {
                    displaySubtitle = searchItem.href;
                  }
                }

                return (
                  <button
                    key={`${category.type}-${('id' in item ? item.id : (item as SearchableItem).name)}`}
                    data-selected={isSelected}
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2 text-left transition-colors',
                      isSelected ? 'bg-primary/10 text-text' : 'text-text hover:bg-surface-hover'
                    )}
                  >
                    <Icon size={18} className={cn(
                      'flex-shrink-0',
                      category.type === 'commands' ? 'text-primary' :
                      category.type === 'records' ? 'text-success' :
                      'text-text-secondary'
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{displayName}</div>
                      {displaySubtitle && (
                        <div className="truncate text-sm text-text-secondary">{displaySubtitle}</div>
                      )}
                    </div>
                    {typeLabel && (
                      <span className="text-xs text-text-secondary bg-surface-hover px-2 py-0.5 rounded">
                        {typeLabel}
                      </span>
                    )}
                    {isSelected && (
                      <CornerDownLeft size={14} className="text-text-secondary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Recent Searches (when no query) */}
          {!query && recentSearches.length > 0 && (
            <div className="border-t border-border mt-2 pt-2">
              <div className="px-4 py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Recent Searches
              </div>
              {recentSearches.map((recent, i) => (
                <button
                  key={i}
                  onClick={() => setQuery(recent)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left text-text-secondary hover:bg-surface-hover hover:text-text transition-colors"
                >
                  <Search size={16} className="flex-shrink-0" />
                  <span className="truncate">{recent}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between gap-4 px-4 py-2 border-t border-border text-xs text-text-secondary">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-surface-hover rounded text-[10px] font-mono">
                <ArrowUp size={10} className="inline" />
              </kbd>
              <kbd className="px-1.5 py-0.5 bg-surface-hover rounded text-[10px] font-mono">
                <ArrowDown size={10} className="inline" />
              </kbd>
              <span className="ml-1">Navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-surface-hover rounded text-[10px] font-mono">Enter</kbd>
              <span>Select</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-surface-hover rounded text-[10px] font-mono">Esc</kbd>
              <span>Close</span>
            </span>
          </div>
          <div className="text-text-secondary/70">
            Press <kbd className="px-1.5 py-0.5 bg-surface-hover rounded text-[10px] font-mono">Shift</kbd> twice to open
          </div>
        </div>
      </div>
    </div>
  );
}
